import React, { useId, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  OutlinedInput,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { ServiceTitleDescriptionItem } from "utils/modals";
import { styles } from "modules/serviceManagement/styles";

interface TitleDescriptionListInputProps {
  label: string;
  titlePlaceholder?: string;
  descriptionPlaceholder?: string;
  value: ServiceTitleDescriptionItem[];
  titleMaxLength?: number;
  descriptionMaxLength?: number;
  error?: string;
  onChange: (next: ServiceTitleDescriptionItem[]) => void;
  addButtonLabel?: string;
  emptyTitle?: string;
  emptyHint?: string;
  disabled?: boolean;
}

const TitleDescriptionListInput: React.FC<TitleDescriptionListInputProps> = ({
  label,
  titlePlaceholder = "Enter title",
  descriptionPlaceholder = "Enter description",
  value,
  titleMaxLength = 150,
  descriptionMaxLength = 500,
  error,
  onChange,
  addButtonLabel = "Add",
  emptyTitle = "Nothing added yet",
  emptyHint = "Use the form above to add your first item.",
  disabled = false,
}) => {
  const uid = useId();
  const [draftTitle, setDraftTitle] = useState("");
  const [draftDescription, setDraftDescription] = useState("");
  const items = Array.isArray(value) ? value : [];
  const canAdd = Boolean(draftTitle.trim());

  const addItem = () => {
    const nextTitle = draftTitle.trim();
    const description = draftDescription.trim();
    if (!nextTitle || disabled) return;
    if (
      items.some(
        (item) =>
          item.title.toLowerCase() === nextTitle.toLowerCase() &&
          `${item.description || ""}`.trim() === description,
      )
    ) {
      setDraftTitle("");
      setDraftDescription("");
      return;
    }
    onChange([...items, { title: nextTitle, description }]);
    setDraftTitle("");
    setDraftDescription("");
  };

  const removeItem = (index: number) => {
    if (disabled) return;
    onChange(items.filter((_, i) => i !== index));
  };

  const onTitleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      addItem();
    }
  };

  return (
    <FormControl fullWidth error={Boolean(error)} sx={{ m: 0 }}>
      {label ? (
        <Typography sx={{ ...styles.mediaLabel, mb: 1.5 }}>{label}</Typography>
      ) : null}
      {error && (
        <FormHelperText error sx={{ mx: 0, mb: 1.5 }}>
          {error}
        </FormHelperText>
      )}

      <Box sx={styles.listRoot}>
        {!disabled ? (
          <Box sx={styles.composer}>
            <Box sx={styles.composerGrid}>
              <FormControl fullWidth>
                <InputLabel htmlFor={`td-title-${uid}`}>Title</InputLabel>
                <OutlinedInput
                  fullWidth
                  id={`td-title-${uid}`}
                  label="Title"
                  value={draftTitle}
                  placeholder={titlePlaceholder}
                  inputProps={{ maxLength: titleMaxLength }}
                  sx={styles.selectDropdown}
                  disabled={disabled}
                  onChange={(e) => setDraftTitle(e.target.value.trimStart())}
                  onKeyDown={onTitleKeyDown}
                />
              </FormControl>
              <FormControl fullWidth>
                <InputLabel htmlFor={`td-desc-${uid}`}>Description</InputLabel>
                <OutlinedInput
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={5}
                  id={`td-desc-${uid}`}
                  label="Description"
                  value={draftDescription}
                  placeholder={descriptionPlaceholder}
                  inputProps={{ maxLength: descriptionMaxLength }}
                  sx={styles.titleDescTextarea}
                  disabled={disabled}
                  onChange={(e) => setDraftDescription(e.target.value)}
                />
              </FormControl>
            </Box>
            <Box sx={styles.composerActions}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<AddRoundedIcon />}
                onClick={addItem}
                disabled={!canAdd || disabled}
                sx={styles.composerButton}
              >
                {addButtonLabel}
              </Button>
            </Box>
          </Box>
        ) : null}

        {items.length === 0 ? (
          <Box sx={styles.emptyState}>
            <Typography sx={styles.emptyStateTitle}>{emptyTitle}</Typography>
            <Typography sx={styles.emptyStateHint}>{emptyHint}</Typography>
          </Box>
        ) : (
          <Box sx={styles.listStack}>
            {items.map((item, index) => (
              <Box key={item._id || `${item.title}-${index}`} sx={styles.listItem}>
                <Box sx={styles.listItemIndex}>{index + 1}</Box>
                <Box sx={styles.listItemBody}>
                  <Typography sx={styles.listItemTitle}>{item.title}</Typography>
                  {item.description ? (
                    <Typography sx={styles.listItemDesc}>
                      {item.description}
                    </Typography>
                  ) : null}
                </Box>
                {!disabled ? (
                  <IconButton
                    size="small"
                    sx={styles.listItemDelete}
                    onClick={() => removeItem(index)}
                    disabled={disabled}
                    aria-label={`Remove ${item.title}`}
                  >
                    <DeleteOutlineRoundedIcon fontSize="small" />
                  </IconButton>
                ) : null}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </FormControl>
  );
};

export default TitleDescriptionListInput;
